<script setup lang="ts">
import { computed } from 'vue'
import { LoaderCircle } from 'lucide-vue-next'

const props = withDefaults(
  defineProps<{
    variant?: 'primary' | 'secondary' | 'tertiary' | 'danger'
    /** on navy surfaces the primary button is sage with navy text */
    onNavy?: boolean
    size?: 'sm' | 'md'
    type?: 'button' | 'submit' | 'reset'
    loading?: boolean
    disabled?: boolean
    block?: boolean
  }>(),
  { variant: 'primary', onNavy: false, size: 'md', type: 'button' },
)

const classes = {
  primaryLight:
    'bg-primary text-on-primary hover:bg-primary-hover active:bg-primary-active focus-visible:ring-focus',
  primaryNavy:
    'bg-sage-400 text-navy-700 hover:bg-sage-300 active:bg-sage-500 focus-visible:ring-sage-400',
  secondary:
    'border border-navy-700 text-navy-700 hover:bg-navy-50 focus-visible:ring-focus dark:border-sage-400 dark:text-sage-200 dark:hover:bg-navy-800',
  tertiary:
    'text-navy-600 underline-offset-4 hover:underline focus-visible:ring-focus dark:text-sage-300',
  danger: 'bg-rose-700 text-white hover:bg-rose-600 active:bg-rose-800 focus-visible:ring-rose-700',
}

const variantClass = computed(() =>
  props.variant === 'primary'
    ? props.onNavy
      ? classes.primaryNavy
      : classes.primaryLight
    : classes[props.variant],
)
const sizeClass = computed(() =>
  props.variant === 'tertiary'
    ? 'px-1 py-1 text-sm'
    : props.size === 'sm'
      ? 'px-3.5 py-1.5 text-sm'
      : 'px-5 py-2.5 text-sm',
)
</script>

<template>
  <button
    :type="type"
    :disabled="disabled || loading"
    :aria-busy="loading || undefined"
    class="inline-flex items-center justify-center gap-2 rounded-full font-medium transition focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
    :class="[variantClass, sizeClass, block && 'w-full']"
  >
    <LoaderCircle v-if="loading" class="size-4 animate-spin" aria-hidden="true" />
    <slot />
  </button>
</template>
